<template>
  <div class="radio-header-section">
    <div class="radio-header-section__inner">
      <h3
        class="radio-header-section__title"
        v-if="!screenLandScape || lockOrientation !== undefined"
      >
        {{ $t('RaceVideoPage.Radio.title') }}
      </h3>

      <div class="radio-header-section__filter">
        <filter-selector-parts
          class="radio-header-section__filter__item"
          defaultItemLabel="Driver"
          defaultListItemLabel="Driver (all)"
          :selectID="driverFilterId"
          :filterList="driverFilterList"
          @filterChange="changeDriverFilter"
        />
        <filter-selector-parts
          class="radio-header-section__filter__item"
          defaultItemLabel="Lap"
          defaultListItemLabel="Lap (all)"
          :selectID="lapFilterId"
          :filterList="lapFilterItems"
          @filterChange="changeLapFilter"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import { sortBy, times, uniq } from 'lodash'
import FilterSelectorParts, {
  FilterList,
} from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/FilterSelectorParts.vue'
import StoreUtil from '@/store/StoreUtil'
import useRadio from '@/components/RaceVideoPage/hook/useRadio'
import { LockOrientationType } from '@/views/RaceVideoPage/RaceVideoPage.vue'

type DataType = {
  driverFilterId: string
  lapFilterId: string
}

/**
 * レース動画再生画面 無線一覧表示 ヘッダーセクションのコンポーネント
 */
export default defineComponent({
  name: 'RadioHeaderSection',
  components: {
    FilterSelectorParts,
  },
  data(): DataType {
    return {
      driverFilterId: '',
      lapFilterId: '',
    }
  },
  props: {
    /**
     * 画面向き
     */
    screenOrientationType: {
      type: String,
      default: 'portrait-primary',
    },
    /**
     * 画面固定
     */
    lockOrientation: {
      type: String as PropType<LockOrientationType>,
    },
  },
  setup(props) {
    const screenLandScape = computed(() => props.screenOrientationType?.indexOf('landscape') !== -1)
    const raceVideoPageStore = StoreUtil.useStore('RaceVideoPageStore')
    const { getAllPlayerRadioDataSet } = useRadio()

    // Lapプルダウンの選択項目を生成する
    // 現状の無線交信データのリストが保持しているLapの情報からリストを生成する。
    // リストが生成できなかった場合、1〜50の選択項目を生成する
    const lapFilterItems = computed<Array<FilterList>>(() => {
      let lapList = sortBy(
        uniq(getAllPlayerRadioDataSet.value.map((radio) => Number(radio.lap))).filter((lap) => lap),
      )

      if (lapList.length === 0) {
        lapList = times(50, String).map((lap) => Number(lap) + 1)
      }
      const lapFilterList: Array<FilterList> = []
      lapList.forEach((lap) => {
        lapFilterList.push({
          id: `${lap}`,
          name: {
            ja: `Lap ${lap}`,
            en: `Lap ${lap}`,
          },
        })
      })
      return lapFilterList
    })

    return {
      screenLandScape,
      driverFilterList: raceVideoPageStore.participatingPlayers,
      lapFilterItems,
    }
  },
  methods: {
    changeDriverFilter(id: string): void {
      this.$emit('changeDriverFilter', id)
    },
    changeLapFilter(id: string): void {
      this.$emit('changeLapFilter', id)
    },
  },
})
</script>

<style lang="scss" scoped>
.radio-header-section {
  width: 100%;
  padding: 0 24px 0 20px;
  background: $black80;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 8%);
  color: $white;

  &__inner {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    max-width: 375px;
    width: 100%;
    height: 44px;
    margin: 0 auto;
  }

  &__title {
    @include fontSetTextMedium();

    letter-spacing: 0.04em;
  }

  &__filter {
    position: relative;
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-end;
    width: calc(100% - 68px);
    height: 36px;
    column-gap: 4px;

    &__item {
      flex: 1;
      max-width: calc(50% - 2px);
    }
  }
}
</style>

<style lang="scss">
.radio-header-section {
  &__filter {
    .filter-selector-parts__list {
      width: clamp(240px, 64vw, 280px);
      max-height: calc(var(--screen-height) - 56.25vw - 44px);
    }
  }
}

.race-video-radio-pane {
  &--landscape-primary,
  &--landscape-secondary {
    .radio-header-section {
      height: 44px;
      padding: 0 6px;

      &__title {
        display: none;
      }

      &__filter {
        width: 100%;
      }
    }

    .filter-selector-parts__list {
      --content-height: calc(var(--screen-height) - 44px);

      width: 100%;
      max-height: calc(var(--screen-height) - 44px);
    }
  }
}
</style>
